import React, { useState, useEffect } from 'react';
import { WORKFLOW_STEP_LABELS, normalizeWorkflowStatus } from '../../../../../models/CatalogManagement';
import type { CMWorkflowStatus } from '../../../../../models/CatalogManagement';
import { Check, Calendar } from 'lucide-react';
import { Form, Spinner } from 'react-bootstrap';
import { StepCircle } from './shared/StepCircle';
import { StatusBadge } from '../../../../../components/ui/badge/StatusBadge';

interface WorkflowOverviewSectionProps {
  currentStatus?: CMWorkflowStatus | undefined;
  wave?: string | undefined;
  onWaveChange?: (wave: string) => void;
  isUpdatingWave?: boolean;
  isWpSheetEditorUsed?: boolean;
}

export const WorkflowOverviewSection: React.FC<WorkflowOverviewSectionProps> = ({
  currentStatus,
  wave,
  onWaveChange,
  isUpdatingWave,
  isWpSheetEditorUsed,
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');
  const currentYearMonth = `${currentYear}-${currentMonth}`;
  const isYear2026OrLater = currentYear >= 2026;

  const [selectedWave, setSelectedWave] = useState<string>(wave || '');

  useEffect(() => {
    if (wave) {
      setSelectedWave(wave);
    } else if (isYear2026OrLater && onWaveChange) {
      onWaveChange(currentYearMonth);
    }
  }, [wave, isYear2026OrLater, currentYearMonth, onWaveChange]);

  const handleWaveChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newWave = e.target.value;
    setSelectedWave(newWave);
    if (onWaveChange && newWave) {
      onWaveChange(newWave);
    }
  };

  const normalizedStatus = normalizeWorkflowStatus(currentStatus);
  const currentStepIndex = normalizedStatus
    ? WORKFLOW_STEP_LABELS.findIndex((step) => step.id === normalizedStatus)
    : -1;

  const getStepStatus = (index: number): 'completed' | 'pending' => {
    const isCompleted = currentStepIndex >= 0 && index <= currentStepIndex;
    return isCompleted ? 'completed' : 'pending';
  };

  const handleStepClick = (index: number) => {
    const sectionId = `step-${index + 1}-section`;
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="workflow-status position-relative" style={{ padding: '20px 10px' }}>
      <div className="d-flex justify-content-center align-items-center gap-3 mb-4">
        {!isWpSheetEditorUsed ? (
          <>
            <div className="d-flex align-items-center gap-2">
              <Calendar size={18} className="text-primary" />
              <Form.Label className="mb-0 fw-semibold" style={{ fontSize: '0.9rem' }}>
                Wave:
              </Form.Label>
            </div>
            {isYear2026OrLater ? (
              <>
                {isUpdatingWave && <Spinner animation="border" size="sm" />}
                <StatusBadge type="primary" label={`Current: ${currentYearMonth}`} size="md" />
              </>
            ) : (
              <>
                <Form.Select
                  size="sm"
                  value={selectedWave}
                  onChange={handleWaveChange}
                  disabled={isUpdatingWave}
                  style={{
                    width: 'auto',
                    minWidth: '120px',
                    borderColor: selectedWave ? undefined : '#dc3545',
                  }}
                >
                  <option value="" disabled>
                    Select Wave...
                  </option>
                  <option value="6">Wave 6</option>
                </Form.Select>
                {isUpdatingWave && <Spinner animation="border" size="sm" />}
                {selectedWave && selectedWave !== '0' && (
                  <StatusBadge type="primary" label={`Current: Wave ${selectedWave}`} size="md" />
                )}
              </>
            )}
          </>
        ) : (
          <StatusBadge type="warning" label="WP Sheet Editor Used" size="md" />
        )}
      </div>
      <div className="d-flex align-items-start justify-content-between position-relative">
        {WORKFLOW_STEP_LABELS.map((step, index) => {
          const status = getStepStatus(index);
          const isLast = index === WORKFLOW_STEP_LABELS.length - 1;

          return (
            <React.Fragment key={step.id}>
              <div
                className="d-flex flex-column align-items-center position-relative flex-shrink-0"
                style={{ zIndex: 2 }}
              >
                <StepCircle stepNumber={index + 1} status={status} size={56} onClick={() => handleStepClick(index)} />

                <div
                  className={`workflow-step-label text-center mt-3 ${
                    status === 'completed' ? 'fw-semibold text-success' : 'text-muted'
                  }`}
                  style={{
                    fontSize: '0.75rem',
                    minWidth: '90px',
                    maxWidth: '110px',
                    lineHeight: '1.4',
                    wordBreak: 'break-word',
                  }}
                >
                  {status === 'completed' ? (
                    <>
                      {step.labelAfter} <Check size={14} className="ms-1" />
                    </>
                  ) : (
                    step.labelBefore
                  )}
                </div>
              </div>

              {!isLast && (
                <div
                  className="workflow-progress-line align-self-start position-relative flex-grow-1"
                  style={{
                    marginTop: '24px',
                    marginLeft: '12px',
                    marginRight: '12px',
                  }}
                >
                  <div
                    style={{
                      height: '3px',
                      width: '100%',
                      borderRadius: '2px',
                      background: status === 'completed' ? 'var(--bs-success)' : 'var(--bs-border-color)',
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
